import React, { useState, useEffect } from 'react';
import './NebulaBackground.css';

// Generate deterministic star positions (computed once at module level)
const STAR_COUNT = 28;
const stars = Array.from({ length: STAR_COUNT }, (_, i) => ({
  id: i,
  left: `${(7 + (i * 37 + i * i * 3) % 89)}%`,
  top: `${(5 + (i * 41 + i * i * 7) % 91)}%`,
  size: 1 + (i % 3),
  delay: `${(i * 1.7) % 8}s`,
  duration: `${3 + (i % 5)}s`,
  // Hide smallest stars on mobile via CSS class
  sizeClass: i % 3 === 0 ? 'nebula-star--lg' : i % 3 === 1 ? 'nebula-star--md' : 'nebula-star--sm',
}));

/**
 * NebulaBackground — PRD-090 Phase 3
 * Replaces StarfieldBackground with a GPU-friendly CSS-only background.
 * Uses a static nebula image with CSS keyframe drift + twinkling star divs.
 * Zero JavaScript runs after initial render.
 */
const NebulaBackground = ({ opacity = 0.45 }) => {
  const [reducedMotion, setReducedMotion] = useState(() => {
    // Check both OS preference and manual toggle
    if (typeof window !== 'undefined') {
      const manualPref = document.documentElement.getAttribute('data-reduce-motion') === 'true';
      const osPref = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      return manualPref || osPref;
    }
    return false;
  });

  useEffect(() => {
    // Listen for OS-level reduced motion changes
    const mediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const handleMediaChange = (e) => setReducedMotion(
      e.matches || document.documentElement.getAttribute('data-reduce-motion') === 'true'
    );

    mediaQuery?.addEventListener?.('change', handleMediaChange);

    // Listen for manual toggle changes via MutationObserver on html element
    const observer = new MutationObserver(() => {
      const manualPref = document.documentElement.getAttribute('data-reduce-motion') === 'true';
      const osPref = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      setReducedMotion(manualPref || osPref);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-reduce-motion'],
    });

    return () => {
      mediaQuery?.removeEventListener?.('change', handleMediaChange);
      observer.disconnect();
    };
  }, []);

  const isStatic = reducedMotion;

  return (
    <div className="nebula-bg" aria-hidden="true">
      {/* Nebula image layer with slow drift — opacity on image only, not vignette */}
      <div
        className={`nebula-bg__image ${isStatic ? 'nebula-bg__image--static' : ''}`}
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/nebula-bg.webp)`, opacity }}
      />

      {/* Dark vignette overlay for text readability */}
      <div className="nebula-bg__vignette" />

      {/* Twinkling star divs */}
      {stars.map((star) => (
        <div
          key={star.id}
          className={`nebula-star ${star.sizeClass} ${isStatic ? 'nebula-star--static' : ''}`}
          style={{
            left: star.left,
            top: star.top,
            width: `${star.size}px`,
            height: `${star.size}px`,
            animationDelay: star.delay,
            animationDuration: star.duration,
          }}
        />
      ))}
    </div>
  );
};

export default NebulaBackground;
